import { useState, useEffect, useRef, useCallback } from 'react';
import { ALL_STEPS } from './PermissionDialog.constants';
import usePermissionTesters from './usePermissionTesters';

interface UsePermissionDialogParams {
  onComplete: () => void;
  onCancel: () => void;
  initialScreenPermissionState?: 'prompt' | 'granted' | 'denied';
}

export default function usePermissionDialog({
  onComplete,
  onCancel,
  initialScreenPermissionState
}: UsePermissionDialogParams) {
  const [currentStep, setCurrentStep] = useState(0);
  const [cameraGranted, setCameraGranted] = useState(false);
  const [micGranted, setMicGranted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isTestingCamera, setIsTestingCamera] = useState(false);
  const [isTestingMic, setIsTestingMic] = useState(false);
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(true);
  const [requiredSteps, setRequiredSteps] = useState([
    'camera',
    'mic',
    'screen'
  ]); // Steps that actually need user action - drives skip logic only,
  // NOT what's rendered in the top stepper bar (see ALL_STEPS above).

  // --- Camera-permission-state tracking ---
  // 'prompt' | 'granted' | 'denied'
  const [cameraPermissionState, setCameraPermissionState] = useState('prompt');
  // true when the user dismissed the native permission dialog with the X
  // (as opposed to explicitly clicking "Block")
  const [cameraDismissed, setCameraDismissed] = useState(false);

  // --- Mic-permission-state tracking (mirrors camera) ---
  const [micPermissionState, setMicPermissionState] = useState('prompt');
  const [micDismissed, setMicDismissed] = useState(false);

  // --- Screen-share-permission-state (no browser Permissions API exists
  // for it). The SDK rejects start() when a single window/tab is shared
  // instead of the entire screen; the parent re-shows this dialog with
  // initialScreenPermissionState='denied' to display the denied image.
  const screenPermissionState = initialScreenPermissionState || 'prompt';

  const streamRef = useRef<MediaStream | null>(null);
  const cameraPermissionStatusRef = useRef<PermissionStatus | null>(null);
  const micPermissionStatusRef = useRef<PermissionStatus | null>(null);
  // Tracks the previous granted values so we only auto-advance on a
  // genuine false -> true transition, not on every render of a step.
  const prevCameraGrantedRef = useRef(false);
  const prevMicGrantedRef = useRef(false);

  // Reacts instantly to camera permission changes (no need to click
  // "Test Camera" again) - fires whenever the browser-level permission
  // state flips, e.g. user blocks/allows via the address bar icon.
  const onCameraPermissionChange = async () => {
    const status = cameraPermissionStatusRef.current;
    if (!status) return;

    setCameraPermissionState(status.state);

    if (status.state === 'denied') {
      // Permanently blocked - update UI instantly
      setCameraGranted(false);
      setCameraDismissed(false);
      setError(null);
      if (streamRef.current) {
        streamRef.current.getVideoTracks().forEach((track) => track.stop());
      }
    } else if (status.state === 'granted') {
      // Permission was (re-)granted - silently re-acquire the stream
      setCameraDismissed(false);
      setError(null);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true
        });
        if (streamRef.current) {
          streamRef.current.getVideoTracks().forEach((track) => {
            streamRef.current.removeTrack(track);
            track.stop();
          });
          stream
            .getVideoTracks()
            .forEach((track) => streamRef.current.addTrack(track));
        } else {
          streamRef.current = stream;
        }
        setCameraGranted(true);
      } catch (e: any) {
        // no-ops
      }
    } else {
      // 'prompt'
      setCameraGranted(false);
    }
  };

  // Reacts instantly to microphone permission changes (mirrors camera) -
  // fires whenever the browser-level permission state flips, e.g. user
  // blocks/allows via the address bar icon.
  const onMicPermissionChange = async () => {
    const status = micPermissionStatusRef.current;
    if (!status) return;

    setMicPermissionState(status.state);

    if (status.state === 'denied') {
      // Permanently blocked - update UI instantly
      setMicGranted(false);
      setMicDismissed(false);
      setError(null);
      if (streamRef.current) {
        streamRef.current.getAudioTracks().forEach((track) => track.stop());
      }
    } else if (status.state === 'granted') {
      // Permission was (re-)granted - silently re-acquire the stream
      setMicDismissed(false);
      setError(null);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true
        });
        if (streamRef.current) {
          streamRef.current.getAudioTracks().forEach((track) => {
            streamRef.current.removeTrack(track);
            track.stop();
          });
          stream
            .getAudioTracks()
            .forEach((track) => streamRef.current.addTrack(track));
        } else {
          streamRef.current = stream;
        }
        setMicGranted(true);
      } catch (e: any) {
        // no-ops
      }
    } else {
      // 'prompt'
      setMicGranted(false);
    }
  };

  // Check which permissions are already granted on mount (without triggering
  // prompts) so already-granted steps get skipped.
  useEffect(() => {
    const checkExistingPermissions = async () => {
      const steps = [];

      // Camera (Permissions API - no prompt)
      try {
        const cameraPermission = await navigator.permissions.query({
          name: 'camera' as any
        });

        cameraPermissionStatusRef.current = cameraPermission;
        setCameraPermissionState(cameraPermission.state);
        // Subscribe to live changes so we don't need another button click
        cameraPermission.onchange = onCameraPermissionChange;

        if (cameraPermission.state === 'granted') {
          // Already granted - silently acquire the stream
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false
          });
          streamRef.current = stream;
          setCameraGranted(true);
          // Already granted on mount, not a fresh grant - mark as seen to
          // avoid an unwanted auto-advance the instant the dialog opens.
          prevCameraGrantedRef.current = true;
        } else {
          steps.push('camera');
        }
      } catch (err) {
        steps.push('camera');
      }

      // Microphone (mirrors camera)
      try {
        const micPermission = await navigator.permissions.query({
          name: 'microphone' as any
        });

        micPermissionStatusRef.current = micPermission;
        setMicPermissionState(micPermission.state);
        micPermission.onchange = onMicPermissionChange;

        if (micPermission.state === 'granted') {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: false,
            audio: true
          });

          // Merge with existing video stream if any
          if (streamRef.current && stream) {
            stream.getAudioTracks().forEach((track) => {
              streamRef.current.addTrack(track);
            });
          } else {
            streamRef.current = stream;
          }
          setMicGranted(true);
          prevMicGrantedRef.current = true;
        } else {
          steps.push('mic');
        }
      } catch (err) {
        steps.push('mic');
      }

      // Screen share: always shown (no Permissions API to check)
      steps.push('screen');

      setRequiredSteps(steps);
      setIsCheckingPermissions(false);

      // If camera and mic are already granted, jump to the screen step
      if (!steps.includes('camera') && !steps.includes('mic')) {
        setCurrentStep(steps.indexOf('screen'));
      }
    };

    checkExistingPermissions();
  }, []);

  // Cleanup - only stops streams if they weren't transferred to the parent
  const cleanup = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (cameraPermissionStatusRef.current) {
      cameraPermissionStatusRef.current.onchange = null;
    }
    if (micPermissionStatusRef.current) {
      micPermissionStatusRef.current.onchange = null;
    }
  };

  const { testCamera, testMicrophone } = usePermissionTesters({
    streamRef,
    cameraPermissionStatusRef,
    micPermissionStatusRef,
    setError,
    setCameraDismissed,
    setCameraGranted,
    setIsTestingCamera,
    setCameraPermissionState,
    setMicDismissed,
    setMicGranted,
    setIsTestingMic,
    setMicPermissionState
  });

  // Handle next step; on the final step, this dialog's job is done - the
  // caller hides it in response to onComplete(), and the unmount-cleanup
  // effect below stops this preview stream then (the SDK opens its own
  // camera/mic/screen streams internally during start(), it never receives
  // this one).
  const handleNext = useCallback(() => {
    const currentStepType = requiredSteps[currentStep];
    const nextStepIndex = currentStep + 1;

    if (currentStepType === 'camera' && !cameraGranted) return;
    if (currentStepType === 'mic' && !micGranted) return;

    if (nextStepIndex < requiredSteps.length) {
      setCurrentStep(nextStepIndex);
    } else {
      onComplete();
    }
  }, [cameraGranted, currentStep, micGranted, onComplete, requiredSteps]);

  // Handle cancel
  const handleCancel = () => {
    cleanup();
    onCancel();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  // Auto-advance the moment camera permission is freshly granted (covers
  // both the button flow and the live onchange listener flow). Only fires
  // on a genuine false -> true transition while sitting on the camera step.
  useEffect(() => {
    const stepType = requiredSteps[currentStep];
    const justGranted = cameraGranted && !prevCameraGrantedRef.current;
    prevCameraGrantedRef.current = cameraGranted;

    if (stepType === 'camera' && justGranted) {
      handleNext();
    }
  }, [cameraGranted, currentStep, handleNext, requiredSteps]);

  // Auto-advance the moment mic permission is freshly granted (mirrors camera).
  useEffect(() => {
    const stepType = requiredSteps[currentStep];
    const justGranted = micGranted && !prevMicGrantedRef.current;
    prevMicGrantedRef.current = micGranted;

    if (stepType === 'mic' && justGranted) {
      handleNext();
    }
  }, [micGranted, currentStep, requiredSteps, handleNext]);

  const currentStepType = requiredSteps[currentStep];

  // Where the current step sits within the FULL fixed step list (not
  // requiredSteps) - this is what drives the always-visible top stepper.
  const currentFullStepIndex = ALL_STEPS.indexOf(currentStepType);

  // The connector that shows the half-solid/half-dashed "in progress"
  // treatment. Normally that's the connector LEAVING the current step
  // (heading to the next, not-yet-reached step). But when the current step
  // is the LAST one, there's no outgoing connector to dash - so the dash
  // shifts to the incoming connector instead, with the dashed half now on
  // the earlier-step side and the solid half landing on the current step.
  const isLastStepCurrent = currentFullStepIndex === ALL_STEPS.length - 1;
  const dashedConnectorIndex = isLastStepCurrent
    ? currentFullStepIndex - 1
    : currentFullStepIndex;

  // Per-step permission state, keyed the same way as ALL_STEPS/stepConfig -
  // drives the alert-circle error badge on the stepper icon.
  const stepPermissionStateByType = {
    camera: cameraPermissionState,
    mic: micPermissionState,
    screen: screenPermissionState
  };

  // Dismissing the native prompt with the X doesn't flip the browser's
  // permission state to 'denied' (it stays 'prompt') - it only sets this
  // local flag - so the error badge needs to check both.
  const stepDismissedByType = {
    camera: cameraDismissed,
    mic: micDismissed,
    screen: false
  };

  // Show the old generic error box only for cases that aren't already
  // covered by the denied-image UI or the dismissed message
  const showGenericCameraError =
    error && cameraPermissionState !== 'denied' && !cameraDismissed;
  const showGenericMicError =
    error && micPermissionState !== 'denied' && !micDismissed;

  return {
    isCheckingPermissions,
    currentStepType,
    currentFullStepIndex,
    isLastStepCurrent,
    dashedConnectorIndex,
    stepPermissionStateByType,
    stepDismissedByType,
    cameraPermissionState,
    cameraDismissed,
    showGenericCameraError,
    cameraGranted,
    micPermissionState,
    micDismissed,
    showGenericMicError,
    micGranted,
    screenPermissionState,
    error,
    isTestingCamera,
    isTestingMic,
    testCamera,
    testMicrophone,
    handleNext,
    handleCancel
  };
}
